import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { isEmpty, uniqBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";
import parse from "html-react-parser";
import useLanguageChange from "../../hooks/useLanguageChange";
import DeleteNoteModal from "./DeleteNoteModal";
import { LogFirebase } from "../../services/firestore";
import { createNote, deleteNote, setNotesList, updateNote } from "../../actions/notesAction";
import CustomToast from "../UI/customToast";
import { NOTES_MAX_LENGTH } from "../../constants/BottomMenuConstants";
import { TOAST_MESSAGE } from "../../constants/ToastMsgLocalize";
import { setIsNotesDrawerOpen } from "../../reducers/common-reducer";
import { CALL_DATA, PublicVariables } from "../../constants/PublicVariables";
import googleEvent from "../../services/googleEvent";
import { EVENT_NAME, EXTRA_PARAM_TYPE } from "../../constants/GTagConstants";

const NotesDrawer = () => {
    const dispatch = useDispatch();
    const {
        createNoteData,
        createNoteError,
        createNoteLoading,
        updateNoteData,
        updateNoteError,
        updateNoteLoading,
        deleteNoteData,
        deleteNoteError,
        deleteNoteLoading,
        notesList,
    } = useSelector((state) => state.notes);
    const { isNotesDrawerOpen } = useSelector((state) => state.commonReducer);

    const [newNoteTitle, setNewNoteTitle] = useState("");
    const [newNoteBody, setNewNoteBody] = useState("");
    const [noteTitle, setNoteTitle] = useState("");
    const [noteBody, setNoteBody] = useState("");
    const [isCreatingNoteActive, setIsCreatingNoteActive] = useState(false);
    const [currentSelectedNoteId, setCurrentSelectedNoteId] = useState(false);
    const [isDeleteNoteMOdalOpen, setIsDeleteNoteMOdalOpen] = useState(false);
    const [dontHighLightNoteBox, setDontHighLightNoteBox] = useState(false);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    useEffect(() => {
        // Focusing Note Body On Creating Time & On Edit Time
        if (isCreatingNoteActive) {
            const newNoteBodyInput = document.getElementById("noteBodyInput");
            newNoteBodyInput.focus();
        } else if (currentSelectedNoteId) {
            const currentSelectedNoteBodyInput = document.getElementById("selectedNoteBodyInput");
            currentSelectedNoteBodyInput?.focus();
            currentSelectedNoteBodyInput?.setSelectionRange(noteBody.length, noteBody.length);
        }
    }, [isCreatingNoteActive, currentSelectedNoteId]);

    const resetNoteTitleBodyStates = (isForNewNote = false) => {
        if (isForNewNote) {
            setNewNoteTitle("");
            setNewNoteBody("");
        } else {
            setNoteTitle("");
            setNoteBody("");
        }
    };

    const handleCloseNotesDrawer = () => {
        dispatch(setIsNotesDrawerOpen(false));
    };

    const handleCloseDeleteNoteModal = () => {
        setIsDeleteNoteMOdalOpen(false);
        setDontHighLightNoteBox(false);
        setCurrentSelectedNoteId(false);
    };

    const handleClickOnNewNote = () => {
        resetNoteTitleBodyStates(true);
        setCurrentSelectedNoteId(false);
        setIsCreatingNoteActive(true);
    };

    const handleOnDeleteIconClick = (e, noteId) => {
        if (createNoteLoading || updateNoteLoading || deleteNoteLoading) return;
        // For if is Create Note is active than don't select any other note
        if (isCreatingNoteActive) e.stopPropagation();

        setIsDeleteNoteMOdalOpen(true);

        if (!currentSelectedNoteId) setDontHighLightNoteBox(true);

        if (!noteId) return;
        setCurrentSelectedNoteId(noteId);
        const selectedNote = notesList.find((note) => note.noteId === noteId);
        setNoteTitle(selectedNote.title);
        setNoteBody(selectedNote.body);
    };

    const updatePreviousSelectedNote = () => {
        if (isCreatingNoteActive) {
            handleOnNoteCreate();
        } else if (currentSelectedNoteId) {
            handleOnNoteUpdate();
        }
    };

    // Delete Note Functionality
    useEffect(() => {
        if (deleteNoteLoading) return;
        if (deleteNoteData) {
            const updatedNoteList = notesList.filter((note) => note.noteId !== currentSelectedNoteId);
            dispatch(setNotesList(updatedNoteList));
            LogFirebase("Note", "Note deleted", { notes_id: currentSelectedNoteId });
            CustomToast.success(localisedValues[TOAST_MESSAGE.NOTE_DELETED_SUCCESSFULLY]);
            setCurrentSelectedNoteId(false);
            resetNoteTitleBodyStates();
            setDontHighLightNoteBox(false);
        } else if (deleteNoteError) {
            LogFirebase("Note", "Note deleted falied", { notes_id: currentSelectedNoteId });
            CustomToast.error(localisedValues[TOAST_MESSAGE.FAILED_DELETE_NOTE]);
        }
        setIsDeleteNoteMOdalOpen(false);
    }, [deleteNoteLoading, deleteNoteData, deleteNoteError]);

    const handleDeleteNote = () => {
        if (isCreatingNoteActive && !currentSelectedNoteId) {
            resetNoteTitleBodyStates(true);
            setIsDeleteNoteMOdalOpen(false);
            setIsCreatingNoteActive(false);
            setDontHighLightNoteBox(false);
            return;
        }
        dispatch(deleteNote({ noteId: currentSelectedNoteId }));
    };

    // Create Note Functionality
    useEffect(() => {
        if (createNoteLoading) return;
        if (createNoteData) {
            const newCreateNoteId = createNoteData.noteId;
            const newNote = {
                title: parse(createNoteData.title),
                body: parse(createNoteData.notes),
                noteId: newCreateNoteId,
                createdAt: Date.now(),
            };

            let updatedNoteList = [newNote, ...notesList];
            updatedNoteList = uniqBy(updatedNoteList, "noteId");
            dispatch(setNotesList(updatedNoteList));
            LogFirebase("Note", "Note added", { notes_id: newCreateNoteId });
            CustomToast.success(localisedValues[TOAST_MESSAGE.NOTE_ADDED_SUCCESSFULLY]);

            const currDate = new Date();
            const formattedDate = moment.utc(currDate).format("DD-MM-YYYY hh:mm:ss A");
            const timestamp = Math.floor(currDate.getTime() / 1000);

            googleEvent.logEvent(EVENT_NAME.NOTE, EXTRA_PARAM_TYPE.obj, {
                NOTE_ID: `${newCreateNoteId}_${timestamp}`,
                IDENTITY: CALL_DATA.localIdentity,
                NOTE_ADDED_ON: formattedDate,
            });
        } else if (createNoteError) {
            LogFirebase("Note", "Note added failed", "");
            CustomToast.error(localisedValues[TOAST_MESSAGE.FAILED_ADD_NOTE]);
        }

        resetNoteTitleBodyStates(true);
        setIsCreatingNoteActive(false);
    }, [createNoteLoading, createNoteData, createNoteError]);

    const handleOnNoteCreate = () => {
        if (createNoteLoading || updateNoteLoading || deleteNoteLoading) return;

        if (isEmpty(newNoteTitle?.trim()) && isEmpty(newNoteBody?.trim())) {
            setIsCreatingNoteActive(false);
            LogFirebase("Note", "Note added failed", "Title and Body not available");
            return;
        }

        const newNote = {
            title: newNoteTitle,
            notes: newNoteBody,
        };
        dispatch(createNote(newNote));
    };

    // Update Note Functionality
    useEffect(() => {
        let editedNoteId = "";
        if (updateNoteLoading) return;
        if (updateNoteData) {
            editedNoteId = updateNoteData.noteId;
            const updatedNoteList = notesList.map((note) => {
                if (note.noteId === editedNoteId) {
                    return {
                        ...note, // Spread existing note properties
                        title: parse(updateNoteData.title),
                        body: parse(updateNoteData.notes),
                        createdAt: Date.now(),
                    };
                }
                return note; // Return unchanged note
            });
            dispatch(setNotesList(updatedNoteList));
            LogFirebase("Note", "Note edited", { notes_id: editedNoteId });
            CustomToast.success(localisedValues[TOAST_MESSAGE.NOTE_EDITED_SUCCESSFULLY]);
        } else if (updateNoteError) {
            LogFirebase("Note", "Note edited falied", { notes_id: editedNoteId });
            CustomToast.error(localisedValues[TOAST_MESSAGE.FAILED_EDIT_NOTE]);
        }

        // if user try to press other notes delete btn than do not set current selected note id to false & reset states
        if (!isDeleteNoteMOdalOpen && currentSelectedNoteId === updateNoteData?.noteId) {
            setCurrentSelectedNoteId(false);
            resetNoteTitleBodyStates();
        }
    }, [updateNoteLoading, updateNoteData, updateNoteError]);

    const handleOnNoteUpdate = () => {
        if (createNoteLoading || updateNoteLoading || deleteNoteLoading) return;
        const selectedNote = {
            title: isEmpty(noteTitle) ? null : noteTitle,
            notes: isEmpty(noteBody) ? null : noteBody,
            noteId: currentSelectedNoteId,
        };

        dispatch(updateNote(selectedNote));
    };

    const handleClickOnNoteBox = (noteId) => {
        if (currentSelectedNoteId === noteId) return;
        updatePreviousSelectedNote();
        setCurrentSelectedNoteId(noteId);
        const selectedNote = notesList.find((note) => note.noteId === noteId);
        setNoteTitle(selectedNote.title);
        setNoteBody(selectedNote.body);
    };

    // Handle Auto Resize Text-area
    const autoResizeNotes = (inputElement, initialHeight, maxHeight, extraHeight, shouldScroll) => {
        if (!inputElement) return;
        if (shouldScroll && inputElement.scrollHeight >= 112) {
            inputElement.scrollTop = inputElement.scrollHeight;
        }

        inputElement.style.height = initialHeight;
        inputElement.style.maxHeight = maxHeight;
        inputElement.style.height = inputElement.scrollHeight + "px";
        inputElement.style.maxHeight = inputElement.scrollHeight + extraHeight + "px";
    };

    const handleNoteTitleInputAutoResize = (isForSelectedNote = false) => {
        if (isForSelectedNote) {
            autoResizeNotes(document.getElementById("selectedNoteTitleInput"), "20px", "40px", 20, false);
        } else {
            autoResizeNotes(document.getElementById("noteTitleInput"), "20px", "40px", 20, true);
        }
    };

    const handleNoteBodyInputAutoResize = (isForSelectedNote = false) => {
        if (isForSelectedNote) {
            autoResizeNotes(document.getElementById("selectedNoteBodyInput"), "40px", "180px", 140, false);
        } else {
            autoResizeNotes(document.getElementById("noteBodyInput"), "40px", "180px", 140, true);
        }
    };

    return (
        <>
            <div className={clsx("sidebar left-sidebar", isNotesDrawerOpen && "active")} id='notes-sidebar'>
                <div className='close-in-mobile' onClick={handleCloseNotesDrawer} onKeyDown={() => {}}></div>
                <div className='sidebar-in'>
                    <div className='sidebar-head'>
                        <div className='sidebar-head-title'>
                            <div className='title'>{localisedValues["notes_lable"]}</div>
                            <div className='head-sub-title'>
                                <div className='head-sub-title-in'>{localisedValues["only_visible_to_you"]}</div>
                            </div>
                        </div>
                        <div className='sidebar-head-right'>
                            <div className='sidebar-close'>
                                <button className='btn close-sidebar-button' onClick={handleCloseNotesDrawer} data-testid='noteDrawerCloseBtn'>
                                    <span className='for-icon icon-close-light'></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='sidebar-body padding-0 custom-scroll'>
                        {isEmpty(notesList) && !isCreatingNoteActive && (
                            <div className='no-sidebar-data'>
                                <div className='no-sidebar-data-in'>
                                    <div className='no-sidebar-data-box text-center'>
                                        <div className='no-data-icon'>
                                            <span className='for-icon icon-no-note'></span>
                                        </div>
                                        <div className='no-data-text'>
                                            <p>{localisedValues["create_new_note_description"]}</p>
                                        </div>
                                        <div className='button-inline d-flex justify-content-center'>
                                            <div className='button'>
                                                <button className='btn btn-primary btn-with-icon' onClick={handleClickOnNewNote}>
                                                    <span className='btn-icon icon-add-circle'></span>
                                                    <span className='btn-text'>{localisedValues["new_note_lable"]}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(!isEmpty(notesList) || isCreatingNoteActive) && (
                            <div className='note-list'>
                                {(!isCreatingNoteActive || currentSelectedNoteId) && (
                                    <div className='add-note-button custom-tooltip top-tooltip tooltip-auto-width m-hide'>
                                        <button className='btn btn-primary' onClick={handleClickOnNewNote}>
                                            <span className='btn-icon icon-add-circle'></span>
                                        </button>
                                        <div className='custom-tooltip-content text-nowrap'>
                                            <div className='custom-tooltip-in'>
                                                <p>{localisedValues["new_note_lable"]}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {isCreatingNoteActive && (
                                    <div className='note-box edit-note-box'>
                                        <div className='note-message'>
                                            <div className='note-icon'>
                                                <span className='for-icon icon-notes'></span>
                                            </div>
                                            <div className='note-message-head'>
                                                <div className='note-message-head-textarea'>
                                                    <textarea
                                                        maxLength={NOTES_MAX_LENGTH.TITLE_MAX_LENGTH}
                                                        className='input-text-area'
                                                        id='noteTitleInput'
                                                        value={newNoteTitle}
                                                        onFocus={() => handleNoteTitleInputAutoResize(false)}
                                                        onChange={(e) => {
                                                            setNewNoteTitle(e.target.value);
                                                            handleNoteTitleInputAutoResize(false);
                                                        }}
                                                        placeholder={localisedValues["title_lable"]}
                                                        style={{ height: "20px", fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div className='note-message-body'>
                                                <div className='note-message-body-textarea'>
                                                    <textarea
                                                        maxLength={NOTES_MAX_LENGTH.BODY_MAX_LENGTH}
                                                        className='input-text-area'
                                                        id='noteBodyInput'
                                                        value={newNoteBody}
                                                        onFocus={() => handleNoteBodyInputAutoResize(false)}
                                                        onChange={(e) => {
                                                            setNewNoteBody(e.target.value);
                                                            handleNoteBodyInputAutoResize(false);
                                                        }}
                                                        placeholder={`${localisedValues["new_note_lable"]}...`}
                                                        style={{ height: "40px", fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='note-info'>
                                            <div className='note-info-left'></div>
                                            <div className='note-action'>
                                                <div className='action-button'>
                                                    <div className='button'>
                                                        <button className='btn btn-primary action-text-btn' onClick={handleOnNoteCreate}>
                                                            <span className='btn-text'>{localisedValues["save"]}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className='action-button'>
                                                    <div className='button custom-tooltip tooltip-auto-width m-hide'>
                                                        <button
                                                            className='btn action-btn'
                                                            data-bs-toggle={isDeleteNoteMOdalOpen && "modal"}
                                                            onClick={handleOnDeleteIconClick}
                                                        >
                                                            <span className='for-icon icon-delete'></span>
                                                        </button>
                                                        <div className='custom-tooltip-content text-nowrap'>
                                                            <div className='custom-tooltip-in'>
                                                                <p>{localisedValues["delete_lable"]}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {notesList?.map((note) => {
                                    const isSelectedNote = currentSelectedNoteId === note.noteId && !dontHighLightNoteBox;
                                    return (
                                        <div
                                            className={clsx("note-box", isSelectedNote && "edit-note-box ")}
                                            key={note.noteId}
                                            onClick={() => handleClickOnNoteBox(note.noteId)}
                                            onKeyDown={() => {}}
                                            data-testid='noteBox'
                                        >
                                            <div className='note-message'>
                                                <div className='note-icon'>
                                                    <span className='for-icon icon-notes'></span>
                                                </div>
                                                <div
                                                    className={clsx(
                                                        "note-message-head",
                                                        !isSelectedNote && isEmpty(note.title) && !isEmpty(note.body) ? "d-none" : ""
                                                    )}
                                                >
                                                    {!isSelectedNote ? (
                                                        <div className='note-message-head-text'>
                                                            {isEmpty(note.title) && isEmpty(note.body) ? localisedValues["empty_note"] : note.title}
                                                        </div>
                                                    ) : (
                                                        <div className='note-message-head-textarea'>
                                                            <textarea
                                                                maxLength={NOTES_MAX_LENGTH.TITLE_MAX_LENGTH}
                                                                className='input-text-area'
                                                                onFocus={() => handleNoteTitleInputAutoResize(true)}
                                                                id={isSelectedNote && "selectedNoteTitleInput"}
                                                                value={noteTitle}
                                                                onChange={(e) => {
                                                                    setNoteTitle(e.target.value);
                                                                    handleNoteTitleInputAutoResize(true);
                                                                }}
                                                                placeholder={localisedValues["title_lable"]}
                                                                style={{ height: "20px", fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='note-message-body'>
                                                    {!isSelectedNote ? (
                                                        <div className='note-message-body-text'>{note.body}</div>
                                                    ) : (
                                                        <div className='note-message-body-textarea'>
                                                            <textarea
                                                                maxLength={NOTES_MAX_LENGTH.BODY_MAX_LENGTH}
                                                                className='input-text-area'
                                                                id={isSelectedNote && "selectedNoteBodyInput"}
                                                                value={noteBody}
                                                                onFocus={() => handleNoteBodyInputAutoResize(true)}
                                                                onChange={(e) => {
                                                                    setNoteBody(e.target.value);
                                                                    handleNoteBodyInputAutoResize(true);
                                                                }}
                                                                placeholder={`${localisedValues["new_note_lable"]}...`}
                                                                style={{ height: "80px", fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='note-info'>
                                                <div className='note-info-left'>
                                                    <div className='note-time'>
                                                        <span className='for-icon icon-time'></span>
                                                        <span className='for-text'>{moment(note.createdAt).format("h:mm A")}</span>
                                                    </div>
                                                </div>
                                                <div className='note-action'>
                                                    {isSelectedNote && (
                                                        <div className='action-button'>
                                                            <div className='button'>
                                                                <button className='btn btn-primary action-text-btn' onClick={handleOnNoteUpdate}>
                                                                    <span className='btn-text'>{localisedValues["save"]}</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className='action-button'>
                                                        <div className='button custom-tooltip tooltip-auto-width m-hide'>
                                                            <button
                                                                className='btn action-btn'
                                                                data-bs-toggle={isDeleteNoteMOdalOpen && "modal"}
                                                                onClick={(e) => handleOnDeleteIconClick(e, note.noteId)}
                                                            >
                                                                <span className='for-icon icon-delete'></span>
                                                            </button>
                                                            <div className='custom-tooltip-content text-nowrap'>
                                                                <div className='custom-tooltip-in'>
                                                                    <p>{localisedValues["delete_lable"]}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {!isSelectedNote && (
                                                        <div className='action-button'>
                                                            <div className='button custom-tooltip tooltip-auto-width m-hide'>
                                                                <button className='btn action-btn'>
                                                                    <span className='for-icon icon-edit'></span>
                                                                </button>
                                                                <div className='custom-tooltip-content text-nowrap'>
                                                                    <div className='custom-tooltip-in'>
                                                                        <p>{localisedValues["edit_label"]}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <DeleteNoteModal
                localisedValues={localisedValues}
                deleteNoteLoading={deleteNoteLoading}
                isDeleteNoteMOdalOpen={isDeleteNoteMOdalOpen}
                handleCloseDeleteNoteModal={handleCloseDeleteNoteModal}
                handleDeleteNote={handleDeleteNote}
            />
        </>
    );
};

export default NotesDrawer;
